import {
  type FC,
  type MouseEvent,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useLocation } from "react-router-dom";

import {
  Button,
  clsx,
  getHashCode,
  Icon,
  type IconName,
  SidebarState,
  Tooltip,
  UIContext,
} from "@alpacahq/ui/old";

export interface SidebarItemProps {
  label: ReactNode;
  leftIcon?: IconName;
  subItems?: ConditionalSidebarItemProps[];
  href?: string;
  active?: boolean;
  newTab?: boolean;
  divider?: boolean;
  unresponsive?: boolean;
  onClick?: () => void;
}

// Items can be false to support conditional rendering in sidebar config arrays.
export type ConditionalSidebarItemProps = false | SidebarItemProps;

const isSidebarItem = (
  item: ConditionalSidebarItemProps,
): item is SidebarItemProps => Boolean(item);

interface SidebarSubItemProps {
  label: ReactNode;
  leftIcon?: IconName;
  active?: boolean;
  isCollapsed?: boolean;
  onClick?: () => void;
}

const SidebarSubItem: FC<SidebarSubItemProps> = ({
  label,
  leftIcon,
  active = false,
  isCollapsed = false,
  onClick,
}) => {
  return (
    <Tooltip content={label} hidden={!isCollapsed} placement="right">
      <Button
        className={clsx(
          "flex h-10 w-full justify-start rounded-lg",
          active && "bg-zinc-100",
        )}
        variant="ghost"
        leftIcon={leftIcon ? <Icon name={leftIcon} /> : undefined}
        onClick={onClick}
      >
        {!isCollapsed && (
          <span className="flex w-full grow justify-start truncate">
            {label}
          </span>
        )}
      </Button>
    </Tooltip>
  );
};

export const SidebarItem: FC<ConditionalSidebarItemProps> = (props) => {
  // if the item is false, don't render anything (used for conditional rendering)
  if (typeof props === "boolean") {
    return null;
  }

  return <SidebarItemContent {...props} />;
};

const SidebarItemContent: FC<SidebarItemProps> = ({
  label,
  leftIcon,
  subItems,
  href,
  newTab = false,
  active = false,
  onClick,
  unresponsive = false,
}) => {
  const { history, sidebarState } = useContext(UIContext);
  const location = useLocation();

  const filteredSubItems = useMemo(
    () => subItems?.filter(isSidebarItem) ?? [],
    [subItems],
  );
  const hasSubItems = !!subItems;

  const isActive = useMemo(() => {
    if (active) {
      return true;
    }
    if (!href) {
      return false;
    }
    const [pathWithQuery = "", hash = ""] = href.split("#");
    const [path = ""] = pathWithQuery.split("?");
    const formattedHash = hash ? `#${hash}` : "";
    const isTemplateHash = hash.includes(":");

    // Items with subItems use exact match only — child highlighting is
    // delegated to activeSubIndex. Standalone items use prefix matching
    // so they stay highlighted on child routes (e.g. /connect on /connect/submit).
    const isPathActive = hasSubItems
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

    return (
      isPathActive &&
      (!formattedHash || isTemplateHash || location.hash === formattedHash)
    );
  }, [active, href, hasSubItems, location.pathname, location.hash]);

  // Find the best-matching sub-item using "most specific prefix wins".
  // This ensures /brokerage/funding/history beats /brokerage/funding,
  // while /brokerage/market-data still matches on /brokerage/market-data/overview.
  const activeSubIndex = useMemo(() => {
    let bestIndex = -1;
    let bestLength = 0;
    for (let index = 0; index < filteredSubItems.length; index++) {
      const subItem = filteredSubItems[index];
      if (subItem.active) {
        return index;
      }
      if (!subItem.href) {
        continue;
      }
      const [pathWithQuery = ""] = subItem.href.split("#");
      const [path = ""] = pathWithQuery.split("?");
      if (!path) {
        continue;
      }
      const matches =
        location.pathname === path || location.pathname.startsWith(`${path}/`);
      if (matches && path.length > bestLength) {
        bestLength = path.length;
        bestIndex = index;
      }
    }
    return bestIndex;
  }, [filteredSubItems, location.pathname]);

  const shouldAutoOpen = isActive || activeSubIndex !== -1;
  const [isManuallyOpen, setIsManuallyOpen] = useState(false);
  const [isAutoOpenSuppressed, setIsAutoOpenSuppressed] = useState(false);
  const isOpen = isManuallyOpen || (!isAutoOpenSuppressed && shouldAutoOpen);

  useEffect(() => {
    if (!shouldAutoOpen) {
      setIsAutoOpenSuppressed(false);
    }
  }, [shouldAutoOpen]);

  // short-form for checking if the sidebar is collapsed
  const isCollapsed = unresponsive
    ? false
    : sidebarState === SidebarState.COLLAPSED;
  const accessibleLabel = typeof label === "string" ? label : "sidebar section";

  const toggleSubItems = () => {
    if (isOpen) {
      setIsManuallyOpen(false);
      setIsAutoOpenSuppressed(shouldAutoOpen);
    } else {
      setIsManuallyOpen(true);
      setIsAutoOpenSuppressed(false);
    }
  };

  const handleItemClick = () => {
    if (hasSubItems && !href) {
      toggleSubItems();
    }

    if (href) {
      if (newTab) {
        window.open(href, "_blank", "noopener,noreferrer");
      } else {
        history?.push(href);
      }
    }

    onClick?.();
  };

  const handleChevronClick = (event: MouseEvent) => {
    event.stopPropagation();
    toggleSubItems();
  };

  return (
    <div className="relative flex flex-col">
      <Tooltip
        content={label}
        hidden={!isCollapsed}
        placement={isCollapsed ? "right" : "top"}
      >
        <div className="relative w-full">
          <Button
            className={clsx(
              "flex h-10 w-full justify-start rounded-lg",
              hasSubItems && !isCollapsed && "pr-8",
              isActive && "bg-zinc-100",
            )}
            variant="ghost"
            leftIcon={leftIcon ? <Icon name={leftIcon} /> : undefined}
            onClick={handleItemClick}
          >
            {!isCollapsed && (
              <span className="flex w-full grow justify-start truncate">
                {label}
              </span>
            )}
          </Button>
          {hasSubItems && !isCollapsed && (
            <button
              type="button"
              aria-expanded={isOpen}
              aria-label={`${isOpen ? "Collapse" : "Expand"} ${accessibleLabel}`}
              className="absolute top-1/2 right-2 flex -translate-y-1/2 rounded p-1 focus:outline-none focus:ring-2 focus:ring-yellow-200"
              onClick={handleChevronClick}
            >
              <Icon
                name={isOpen ? "ChevronDown" : "ChevronRight"}
                className="h-4 w-4 stroke-[3px]"
              />
            </button>
          )}
        </div>
      </Tooltip>
      {isOpen && (
        <div
          className={clsx(
            "mt-0.5 flex flex-col gap-0.5",
            !isCollapsed && "pl-2",
          )}
        >
          {filteredSubItems.map((subItem, idx) => (
            <SidebarSubItem
              key={getHashCode(subItem)}
              label={subItem.label}
              leftIcon={subItem.leftIcon}
              active={idx === activeSubIndex}
              isCollapsed={isCollapsed}
              onClick={() => {
                if (subItem.href) {
                  if (subItem.newTab) {
                    window.open(subItem.href, "_blank", "noopener,noreferrer");
                  } else {
                    history?.push(subItem.href);
                  }
                }
                subItem.onClick?.();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SidebarItem;
